import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _range from 'lodash/range';
import _trim from 'lodash/trim';
import moment from 'moment-timezone';
//Local files
import { CardItem } from './ReminderFlows.styled';
import ReminderAnalysesList from 'components/Analyses/ReminderAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useReminderFlows from 'hooks/useReminderFlows';
import useReminderAnalyses from 'hooks/useReminderAnalyses';
import { DELETE_REMINDER_FLOW } from 'helpers/confirmations';
import { checkEmptyString, needToUpdateField } from 'helpers';
import { timezones } from 'helpers/constants';
import { reminderFlowsSelectors } from "features/reminderFlows";

const MIN_DAYS = 1;
const MAX_DAYS = 7;

const ReminderFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getReminderFlow, createReminderFlow, updateReminderFlow } = useReminderFlows();
  const { getReminderAnalyses, clearLocalReminderAnalyses } = useReminderAnalyses();
  const id = useCustomSelector(reminderFlowsSelectors.getReminderFlowId);
  const reminderFlow = useCustomSelector(reminderFlowsSelectors.getReminderFlowData);
  const fetching = useCustomSelector(reminderFlowsSelectors.reminderFlowIsFetching);
  const [beforeIn, setBeforeIn] = useState('P1D');
  const [sendAt, setSendAt] = useState({ value: '', formattedValue: null, error: '' });
  const [timezone, setTimezone] = useState('US/Eastern');
  const [textTranslations, setTextTranslations] = useState({ en: '', es: '', error: '' });
  const beforeInArray = _map(_range(MIN_DAYS, MAX_DAYS + 1), day => `P${day}D`);
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (reminder_flow) => {
    const todayDate = moment().startOf('day');
    const sendAtFromServer = moment.utc(reminder_flow?.send_at);
    const sendAtWithTodayDate = sendAtFromServer
      .set({
        year: todayDate.year(),
        month: todayDate.month(),
        date: todayDate.date(),
      })
      .tz(reminder_flow?.time_zone);
    const sendAtFinal = sendAtWithTodayDate.format('YYYY-MM-DDTHH:mm:ss');
    
    setBeforeIn(reminder_flow?.before_in);
    setSendAt({ value: sendAtFinal, formattedValue: new Date(sendAtFinal), error: '' });
    setTimezone(reminder_flow?.time_zone);
    setTextTranslations({ 
      en: reminder_flow?.text_translations?.en || '',
      es: reminder_flow?.text_translations?.es || '', 
      error: '' 
    });
  };
  const handleExited = () => {
    setBeforeIn('P1D');
    setSendAt({ value: '', formattedValue: null, error: '' });
    setTimezone('US/Eastern');
    setTextTranslations({ en: '', es: '', error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    if (!checkEmptyString(sendAt.value)) {
      setSendAt(prev => ({ ...prev, error: `Send At can't be empty` }) );
      hasError = true;
    }
    
    if (hasError) return;

    const sendAtUTC = moment.tz(sendAt.value, timezone).utc().format();

    const reminder_flow = {
      ...(needToUpdateField(reminderFlow?.before_in, beforeIn) && { before_in: beforeIn }),
      ...(needToUpdateField(reminderFlow?.send_at, sendAtUTC) && { send_at: sendAtUTC }),
      ...(needToUpdateField(reminderFlow?.time_zone, timezone) && { time_zone: timezone }),
      ...((needToUpdateField(reminderFlow?.text_translations?.en, textTranslations.en) || needToUpdateField(reminderFlow?.text_translations?.es, textTranslations.es)) && {
        text_translations: {
          ...(needToUpdateField(reminderFlow?.text_translations?.en, textTranslations.en) && { en: textTranslations.en }),
          ...(needToUpdateField(reminderFlow?.text_translations?.es, textTranslations.es) && { es: textTranslations.es }),
        }
      })
    };

    if (!_isEmpty(reminder_flow)) {
      if (!!id) {
        updateReminderFlow(reminder_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createReminderFlow(reminder_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_REMINDER_FLOW });
  const fetchReminderAnalyses = useCallback(() => {
    getReminderAnalyses({ offset: 0 });
  }, [getReminderAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalReminderAnalyses();
    };
  }, [id, clearLocalReminderAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      setFirstTimeOpened(false);
      getReminderFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { reminder_flow } = payload;
          handleEntering(reminder_flow);
          fetchReminderAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getReminderFlow, fetchReminderAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              margin='dense'
              select
              disabled={fetching || (!!id && !edit)}
              label='Days Before'
              value={beforeIn}
              onChange={({ target: { value } }) => setBeforeIn(value)}
            >
              {_map(beforeInArray, n => <MenuItem key={n} value={n}>{_trim(n, 'PD')}</MenuItem>)}
            </TextField>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TimePicker
                label="Send at"
                value={sendAt.formattedValue}
                disabled={fetching || (!!id && !edit)}
                onChange={(newValue) => {
                  const newDate = !!newValue && newValue?.toString() !== 'Invalid Date' ? newValue : '';
                  const formattedValue = !!newDate ? new Date(newDate) : null;
                  const value = !!newDate ? moment(newDate).format('YYYY-MM-DDTHH:mm:ss') : '';
                  setSendAt({
                    value,
                    formattedValue,
                    error: !newDate ? 'Date is invalid' : '',
                  });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    margin='dense' 
                    helperText={sendAt.error} 
                    error={!!sendAt.error} 
                  />
                )}
              />
            </LocalizationProvider>
            <TextField
              fullWidth
              margin='dense'
              select
              label='Timezone'
              value={timezone}
              onChange={({ target: { value } }) => setTimezone(value)}
              disabled={fetching || (!!id && !edit)}
            >
              {timezones.map((tz) => (
                <MenuItem key={tz} value={tz}>{tz}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              margin='dense'
              label='Message (en)'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={textTranslations.error}
              error={!!textTranslations.error}
              value={textTranslations.en}
              onChange={({ target: { value } }) => setTextTranslations(prev => ({ ...prev, en: value }))}
            />
             <TextField
              fullWidth
              margin='dense'
              label='Message (es)'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={textTranslations.error}
              error={!!textTranslations.error}
              value={textTranslations.es}
              onChange={({ target: { value } }) => setTextTranslations(prev => ({ ...prev, es: value }))}
            />
            <Typography width='100%' textAlign='center' variant='caption'>{'{phone} {start_at} {location_name} {location_address} {clinician_name} {appointment_type}'}</Typography>
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <ReminderAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <ReminderFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);